import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  GraduationCap,
  Play,
  Clock,
  Users,
  BookOpen,
  Video,
  Calendar,
  Award,
  ArrowRight,
  CheckCircle2,
  Star,
  Download,
  Key,
  Percent,
  ExternalLink,
  Lock,
  Loader2,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AcademiaPage = () => {
  const [selectedTab, setSelectedTab] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('upim_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Load videos
      const videosRes = await fetch(`${API_URL}/api/videos?limit=20`, { headers });
      const videosData = await videosRes.json();
      setVideos(videosData.videos || []);
      
      // Load webinars
      const webinarsRes = await fetch(`${API_URL}/api/webinars?upcoming_only=false&limit=10`, { headers });
      const webinarsData = await webinarsRes.json();
      setWebinars(webinarsData.webinars || []);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const CATEGORY_LABELS = {
    masterclass: 'Master Class',
    tutorial: 'Tutorial',
    conferencia: 'Conferencia',
    webinar: 'Webinar',
    legal: 'Legal',
    fiscal: 'Fiscal',
    ventas: 'Ventas',
    marketing: 'Marketing',
    tecnico: 'Técnico'
  };

  const ACCESS_LABELS = {
    free: 'Gratuito',
    affiliate: 'Afiliados',
    vip: 'VIP'
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
              <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
              Academia UPIM
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-testid="academia-title">
              Formación profesional inmobiliaria
            </h1>
            <p className="text-xl text-gray-600">
              Accede a cursos, certificaciones, webinars y recursos exclusivos para 
              potenciar tu carrera en el sector inmobiliario.
            </p>
          </div>
        </div>
      </section>

      {/* Acceso Unificado - NUEVO */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Key className="h-5 w-5 text-white" />
                </div>
                <span className="text-gray-400 uppercase tracking-wider text-sm font-medium">
                  Acceso Simplificado
                </span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6" data-testid="unified-access-title">
                Un solo usuario y contraseña para todo
              </h2>
              <p className="text-gray-400 text-lg mb-6 leading-relaxed">
                Accede a <strong className="text-white">TODAS las capacitaciones, webinars y recursos</strong> de 
                la Academia UPIM con las mismas credenciales de tu cuenta de afiliado. 
                Sin registros adicionales, sin complicaciones.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-300">Mismo usuario y contraseña de tu cuenta UPIM</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-300">Acceso instantáneo sin re-registro</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-300">Grabaciones y material disponible 24/7</span>
                </li>
              </ul>
              <Button 
                size="lg" 
                className="bg-white text-gray-900 hover:bg-gray-100"
                asChild
              >
                <Link to="/login" data-testid="access-academia-btn">
                  Acceder a la Academia
                  <Lock className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div className="relative hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
                alt="Academia UPIM"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Capacitación Gratuita Semanal - NUEVO */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&h=400&fit=crop"
                alt="Master Classes Semanales"
                className="rounded-2xl shadow-lg"
              />
            </div>
            <div className="order-1 lg:order-2">
              <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
                <Star className="h-3.5 w-3.5 mr-1.5" />
                Incluido en tu membresía
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" data-testid="weekly-training-title">
                Capacitación GRATUITA cada semana
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Como afiliado UPIM tienes acceso a <strong>sesiones de capacitación en vivo cada semana</strong>, 
                totalmente gratuitas e incluidas en tu membresía. Aprende de los mejores expertos 
                del sector inmobiliario mexicano.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900">52+</p>
                  <p className="text-sm text-gray-600">Sesiones al año</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900">$0</p>
                  <p className="text-sm text-gray-600">Costo adicional</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Master Classes en vivo cada semana</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Grabaciones disponibles si no puedes asistir</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Temas actuales y relevantes del mercado</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Sesiones interactivas con Q&A</span>
                </li>
              </ul>
              
              <Button className="bg-gray-900 hover:bg-gray-800" asChild>
                <Link to="/calendario" data-testid="view-calendar-btn">
                  <Calendar className="mr-2 h-4 w-4" />
                  Ver Calendario de Sesiones
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{videos.length}+</p>
              <p className="text-sm text-gray-600">Videos disponibles</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">52+</p>
              <p className="text-sm text-gray-600">Sesiones gratuitas al año</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">500+</p>
              <p className="text-sm text-gray-600">Profesionales certificados</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">4.9</p>
              <p className="text-sm text-gray-600">Calificación promedio</p>
            </div>
          </div>
        </div>
      </section>

      {/* Descuentos en Certificaciones - NUEVO */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
              <Percent className="h-3.5 w-3.5 mr-1.5" />
              Descuentos Exclusivos
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4" data-testid="discounts-title">
              Precios especiales para afiliados
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Como afiliado UPIM obtienes descuentos significativos en certificaciones, 
              cursos especializados y capacitaciones externas.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border border-gray-100 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                  <Award className="h-6 w-6 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Certificación CONOCER</h3>
                <p className="text-gray-600 text-sm mb-4">EC0110.02 Asesoría en Comercialización de Bienes Inmuebles</p>
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-2xl font-bold text-gray-900">$3,479</span>
                  <span className="text-sm text-gray-500 line-through">$4,639</span>
                </div>
                <Badge variant="outline" className="text-gray-700 border-gray-300">
                  Ahorro de $1,160
                </Badge>
              </CardContent>
            </Card>

            <Card className="border border-gray-100 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                  <BookOpen className="h-6 w-6 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Cursos del Colegio Nacional</h3>
                <p className="text-gray-600 text-sm mb-4">Acceso a cursos del Colegio Nacional de Inmobiliarios</p>
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-2xl font-bold text-gray-900">15-25%</span>
                  <span className="text-sm text-gray-500">descuento</span>
                </div>
                <Badge variant="outline" className="text-gray-700 border-gray-300">
                  En cursos seleccionados
                </Badge>
              </CardContent>
            </Card>

            <Card className="border border-gray-100 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                  <Video className="h-6 w-6 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Webinars Premium</h3>
                <p className="text-gray-600 text-sm mb-4">Sesiones especializadas con expertos internacionales</p>
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-2xl font-bold text-gray-900">$0</span>
                  <span className="text-sm text-gray-500">para afiliados</span>
                </div>
                <Badge variant="outline" className="text-gray-700 border-gray-300">
                  Incluido en membresía
                </Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Content Tabs */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-12">
              <TabsTrigger value="videos" className="flex items-center" data-testid="tab-videos">
                <Play className="h-4 w-4 mr-2" />
                Videos
              </TabsTrigger>
              <TabsTrigger value="webinars" className="flex items-center" data-testid="tab-webinars">
                <Video className="h-4 w-4 mr-2" />
                Webinars
              </TabsTrigger>
              <TabsTrigger value="certificaciones" className="flex items-center" data-testid="tab-certificaciones">
                <Award className="h-4 w-4 mr-2" />
                Certificaciones
              </TabsTrigger>
            </TabsList>

            {/* Videos Tab - Connected to API */}
            <TabsContent value="videos">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-12">
                  <Video className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No hay videos disponibles aún</p>
                  <p className="text-sm text-gray-400 mt-2">Pronto agregaremos contenido educativo</p>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videos.map((video) => (
                      <Card 
                        key={video.id} 
                        className="overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 group cursor-pointer"
                        data-testid={`video-card-${video.id}`}
                        onClick={() => setSelectedVideo(video)}
                      >
                        <div className="relative h-48 overflow-hidden bg-gray-100">
                          {video.thumbnail_url ? (
                            <img
                              src={video.thumbnail_url}
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="h-12 w-12 text-gray-300" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                              <Play className="w-6 h-6 text-gray-900 ml-1" />
                            </div>
                          </div>
                          <div className="absolute top-4 left-4">
                            <Badge className="bg-gray-900">{CATEGORY_LABELS[video.category] || video.category}</Badge>
                          </div>
                          {video.is_featured && (
                            <div className="absolute top-4 right-4">
                              <Badge className="bg-yellow-500 text-yellow-900">
                                <Star className="h-3 w-3 mr-1" />
                                Destacado
                              </Badge>
                            </div>
                          )}
                        </div>
                        <CardContent className="p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors line-clamp-2">
                            {video.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">{video.description}</p>
                          
                          <div className="flex items-center text-sm text-gray-500 mb-4">
                            {video.instructor_name && (
                              <>
                                <Users className="h-4 w-4 mr-1" />
                                <span className="mr-4">{video.instructor_name}</span>
                              </>
                            )}
                            <Eye className="h-4 w-4 mr-1" />
                            <span>{video.view_count || 0} vistas</span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <Badge variant="outline" className={`
                              ${video.access_level === 'free' ? 'text-green-700 border-green-300 bg-green-50' : ''}
                              ${video.access_level === 'affiliate' ? 'text-blue-700 border-blue-300 bg-blue-50' : ''}
                              ${video.access_level === 'vip' ? 'text-purple-700 border-purple-300 bg-purple-50' : ''}
                            `}>
                              {ACCESS_LABELS[video.access_level] || 'Gratuito'}
                            </Badge>
                            <Button size="sm" className="bg-gray-900 hover:bg-gray-800">
                              <Play className="h-4 w-4 mr-1" />
                              Ver video
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Video Player Modal */}
                  {selectedVideo && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedVideo(null)}>
                      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="aspect-video bg-black">
                          {selectedVideo.embed_url ? (
                            <iframe
                              src={selectedVideo.embed_url}
                              title={selectedVideo.title}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white">
                              <p>Video no disponible</p>
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedVideo.title}</h3>
                          <p className="text-gray-600 mb-4">{selectedVideo.description}</p>
                          {selectedVideo.instructor_name && (
                            <p className="text-sm text-gray-500">Instructor: {selectedVideo.instructor_name}</p>
                          )}
                          <Button className="mt-4" variant="outline" onClick={() => setSelectedVideo(null)}>
                            Cerrar
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* Webinars Tab - Connected to API */}
            <TabsContent value="webinars">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : webinars.length === 0 ? (
                <div className="text-center py-12">
                  <Video className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No hay webinars disponibles</p>
                  <Button className="mt-4" asChild>
                    <Link to="/calendario">Ver Calendario</Link>
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {webinars.map((webinar) => (
                    <Card 
                      key={webinar.id} 
                      className="border border-gray-100 hover:shadow-lg transition-all duration-300"
                      data-testid={`webinar-card-${webinar.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center">
                            <Video className="h-6 w-6 text-gray-700" />
                          </div>
                          <Badge className={
                            webinar.status === 'scheduled' ? 'bg-gray-900' : 
                            webinar.status === 'completed' ? 'bg-green-600' : 
                            'bg-gray-500'
                          }>
                            {webinar.status === 'scheduled' ? 'Próximamente' : 
                             webinar.status === 'completed' ? 'Grabación Disponible' : 
                             'En Vivo'}
                          </Badge>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{webinar.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{webinar.description}</p>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                          {webinar.date && (
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {new Date(webinar.date).toLocaleDateString('es-MX', { 
                                day: 'numeric', 
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          )}
                          {webinar.time && (
                            <span className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {webinar.time} hrs
                            </span>
                          )}
                          {webinar.registered_count !== undefined && (
                            <span className="flex items-center">
                              <Users className="h-4 w-4 mr-1" />
                              {webinar.registered_count} registrados
                            </span>
                          )}
                        </div>
                        
                        <Button className="w-full bg-gray-900 hover:bg-gray-800" asChild>
                          <Link to={`/webinars/${webinar.id}`}>
                            {webinar.status === 'scheduled' ? 'Registrarme' : 
                             webinar.status === 'completed' ? 'Ver grabación' : 
                             'Unirse'}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Certifications Tab */}
            <TabsContent value="certificaciones">
              <div className="max-w-4xl mx-auto">
                <Card className="overflow-hidden border border-gray-200 shadow-lg">
                  <div className="grid lg:grid-cols-2">
                    <div className="relative h-64 lg:h-auto">
                      <img
                        src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=400&fit=crop"
                        alt="Certificación CONOCER"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent lg:hidden" />
                    </div>
                    <CardContent className="p-8">
                      <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
                        <Award className="h-3.5 w-3.5 mr-1.5" />
                        Certificación Oficial
                      </Badge>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        EC0110.02 - Asesoría en Comercialización de Bienes Inmuebles
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Certifícate oficialmente como profesional inmobiliario con el estándar 
                        de competencia avalado por CONOCER.
                      </p>
                      
                      <div className="bg-gray-50 rounded-xl p-4 mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">El programa incluye:</h4>
                        <ul className="space-y-2">
                          <li className="flex items-center space-x-2">
                            <CheckCircle2 className="h-4 w-4 text-gray-700" />
                            <span className="text-sm text-gray-600">8 horas de Taller de Alineación (2 días)</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <CheckCircle2 className="h-4 w-4 text-gray-700" />
                            <span className="text-sm text-gray-600">Evaluación oficial (3 horas)</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <CheckCircle2 className="h-4 w-4 text-gray-700" />
                            <span className="text-sm text-gray-600">Certificado CONOCER oficial</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <CheckCircle2 className="h-4 w-4 text-gray-700" />
                            <span className="text-sm text-gray-600">Material de estudio incluido</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-gray-900 text-white rounded-xl p-4 text-center">
                          <p className="text-sm text-gray-400 mb-1">Afiliados UPIM</p>
                          <p className="text-2xl font-bold">$3,479</p>
                          <p className="text-xs text-gray-400">+ IVA</p>
                        </div>
                        <div className="bg-gray-100 rounded-xl p-4 text-center">
                          <p className="text-sm text-gray-600 mb-1">No Afiliados</p>
                          <p className="text-2xl font-bold text-gray-700">$4,639</p>
                          <p className="text-xs text-gray-500">+ IVA</p>
                        </div>
                      </div>
                      
                      <Button className="w-full bg-gray-900 hover:bg-gray-800" asChild>
                        <Link to="/afiliacion" data-testid="certification-signup-btn">
                          Inscribirme a la certificación
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </div>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Biblioteca Digital
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Recursos descargables
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Accede a templates, contratos, ebooks y material exclusivo para afiliados.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Templates de Publicidad', icon: Star, count: '20+' },
              { title: 'Contratos y Formatos', icon: BookOpen, count: '15+' },
              { title: 'Master Classes Grabadas', icon: Video, count: '100+' },
              { title: 'Ebooks Inmobiliarios', icon: Download, count: '10+' },
            ].map((resource, index) => (
              <Card key={index} className="border border-gray-100 hover:shadow-lg transition-all group">
                <CardContent className="p-6 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-900 transition-colors">
                    <resource.icon className="h-7 w-7 text-gray-700 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{resource.title}</h3>
                  <p className="text-sm text-gray-500 mb-3">{resource.count} recursos</p>
                  <Button variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900" asChild>
                    <Link to="/recursos">
                      Ver recursos
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Potencia tu carrera inmobiliaria
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Accede a toda la formación y recursos exclusivos con tu membresía UPIM.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12"
            asChild
          >
            <Link to="/afiliacion" data-testid="cta-affiliate-btn">
              Afíliate por $3,500/año
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default AcademiaPage;
